import type { TailoredpayThreeDSResult, TailoredpayTokenizeResult } from '../constants';
import { getPublicKeys } from '../utils/storage';

import { tokenize } from './collect';
import { authenticateThreeDS } from './three-ds';

type BillingContact = {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  address1?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
};

type Params = {
  /** Amount charged right now — the trial amount for a trial checkout. */
  amount: string | number;
  currency: string;
  billing?: BillingContact;
  onChallenge?: () => void;
};

export type TailoredpayPaymentData = TailoredpayTokenizeResult & {
  three_ds?: TailoredpayThreeDSResult;
};

/**
 * Tokenize the card and, when the gateway has payer authentication turned on,
 * run 3DS — in that order, because NMI expects the transaction to already carry
 * the cavv (unlike Stripe/Recurly, which ask for authentication after the fact).
 *
 * Returns everything the `/nmi/checkout` body needs from the browser.
 */
export const collectPaymentData = async ({
  amount,
  currency,
  billing,
  onChallenge,
}: Params): Promise<TailoredpayPaymentData> => {
  const config = getPublicKeys()?.tailoredpay;

  const tokenized = await tokenize();

  // Key for Gateway.js, in order of preference:
  //   1. `gateway_key` from /nmi/config — the moment the backend sends one.
  //   2. `REACT_APP_TAILOREDPAY_GATEWAY_KEY` — a public Checkout key
  //      (`checkout_public_…`) from the gateway's Security Keys, kept in env until
  //      the backend ships it. Public by design: it ends up in the page either way.
  //   3. The Collect.js tokenization key. NMI's "Running 3DS with Collect.js" guide
  //      says `Gateway.create()` takes the same value as `data-tokenization-key`,
  //      but on this account Gateway.js rejected it — so it stays only as a last
  //      resort, and a rejection just skips 3DS (the payment still goes through).
  const gatewayKey =
    config?.gateway_key
    || process.env.REACT_APP_TAILOREDPAY_GATEWAY_KEY
    || config?.tokenization_key;
  const canRun3DS = Boolean(config?.three_ds_enabled && gatewayKey);

  if (!canRun3DS) {
    if (config?.three_ds_enabled && !gatewayKey) {
      // The backend says 3DS is on but hasn't sent the Gateway.js key. Continue
      // without authentication rather than blocking the payment — the gateway
      // decides whether to accept it and carries the liability.
      //
      // The keys present in /nmi/config are listed too: the brief never spells out
      // the field names for the Gateway.js pair, so if the backend ships them under
      // different names this says so outright instead of leaving 3DS quietly off.
      console.warn(
        '[TailoredPay] three_ds_enabled is true but no key for Gateway.js is '
        + 'available (neither `gateway_key` nor `tokenization_key` in /nmi/config) '
        + '— skipping 3DS. Keys received:',
        Object.keys(config || {}),
      );
    }
    return tokenized;
  }

  const three_ds = await authenticateThreeDS({
    gatewayKey: gatewayKey!,
    paymentToken: tokenized.payment_token,
    amount,
    currency,
    ...billing,
    onChallenge,
  });

  return three_ds ? { ...tokenized, three_ds } : tokenized;
};
